import React, { useEffect, useState, useMemo } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { computeLandingUrl } from "@/lib/trackingUrl";
import { ShoppingBag, Copy, ExternalLink, Activity, TrendingUp, Coins, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";

function _fmtMoney(n, cur = "GYD") {
  const v = Number(n);
  if (!isFinite(v)) return "—";
  return `${cur} ${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function _fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}

function _publicBase() {
  // Fallback for the rare case where a row is missing tracking_url. The
  // backend now returns tracking_url per row using PUBLIC_BASE_URL, so the
  // copy-link button gives the affiliate the trackmatrix.net URL their
  // shoppers actually see — not the preview/dashboard origin.
  return typeof window !== "undefined" ? window.location.origin : "";
}

export default function SharesTimelinePage() {
  const [shares, setShares] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.get("/share/my-shares?limit=100");
      setShares(data.shares || []);
    } catch (e) {
      setShares([]);
      const msg = e.response?.data?.detail || e.message || "Couldn't load your shares";
      setLoadError(typeof msg === "string" ? msg : "Couldn't load your shares");
      toast.error("Couldn't load your shares — try Refresh");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Roll-up totals. Clicks / conversions / credit are link-level (credit_scope
  // === 'link'), so we sum them ONCE per link_id — otherwise an affiliate
  // who shared 2+ products from the same host sees their numbers doubled.
  // Products-shared stays per-row.
  const totals = useMemo(() => {
    if (!shares) return null;
    const seenLinks = new Set();
    return shares.reduce(
      (acc, s) => {
        if (s.link_id && s.credit_scope === "link") {
          if (!seenLinks.has(s.link_id)) {
            seenLinks.add(s.link_id);
            acc.clicks += s.clicks || 0;
            acc.conversions += s.conversions || 0;
            acc.credit += Number(s.credit_total) || 0;
          }
        } else {
          // Per-product scope (future-proof) — sum every row.
          acc.clicks += s.clicks || 0;
          acc.conversions += s.conversions || 0;
          acc.credit += Number(s.credit_total) || 0;
        }
        return acc;
      },
      { clicks: 0, conversions: 0, credit: 0 }
    );
  }, [shares]);

  return (
    <DashboardLayout>
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow text-[#002FA7] flex items-center gap-2">
            <Activity size={12} /> Affiliate · Recent shares
          </div>
          <h1 className="text-4xl sm:text-5xl tracking-tight font-black text-[#111827]">Your shares</h1>
          <p className="text-sm text-[#4B5563] mt-2 max-w-2xl">
            Every product you've shared, in order. See which links are getting clicks and conversions so you know what to double down on.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="shares-refresh-button"
          className="text-xs font-bold text-[#002FA7] inline-flex items-center gap-1 border border-[#E5E7EB] rounded-md px-3 py-1.5 hover:border-[#002FA7]"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Summary tiles */}
      {totals && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="shares-summary">
          <SummaryTile icon={ShoppingBag} label="Products shared" value={shares.length} />
          <SummaryTile icon={Activity} label="Total clicks" value={totals.clicks} />
          <SummaryTile icon={TrendingUp} label="Conversions" value={totals.conversions} />
          <SummaryTile icon={Coins} label="Credit earned" value={_fmtMoney(totals.credit, "GYD")} accent />
        </div>
      )}

      {loadError && (
        <div className="rounded-md border border-[#FCA5A5] bg-[#FEF2F2] p-3 mb-4 flex items-start gap-2" data-testid="shares-load-error">
          <AlertCircle size={16} className="text-[#B91C1C] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#7F1D1D] leading-relaxed flex-1">
            <span className="font-bold">Couldn't load your shares.</span> {loadError}
          </div>
        </div>
      )}

      {shares === null ? (
        <div className="space-y-3" data-testid="shares-loading">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-[#F9FAFB] rounded animate-pulse" />
          ))}
        </div>
      ) : shares.length === 0 ? (
        <div className="card-flat p-10 text-center" data-testid="shares-empty">
          <ShoppingBag size={32} className="mx-auto text-[#9CA3AF] mb-3" />
          <div className="font-display font-black text-2xl text-[#111827] mb-1">No shares yet</div>
          <p className="text-sm text-[#4B5563] max-w-md mx-auto">
            Pick a product on a partner site (e.g. <span className="font-mono">guyanadiscounts.com</span>), click <span className="font-bold">"Share to earn"</span>, and your activity will start showing here.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="shares-list">
          {shares.map((s, idx) => (
            <ShareCard key={`${s.link_id}_${s.product_id || idx}`} share={s} />
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}

function SummaryTile({ icon: Icon, label, value, accent }) {
  return (
    <div className={`card-flat p-4 ${accent ? "border-[#A7F3D0] bg-[#ECFDF5]" : ""}`}>
      <div className="label-eyebrow text-[#4B5563] flex items-center gap-1.5">
        <Icon size={11} /> {label}
      </div>
      <div className={`font-display font-black text-2xl mt-1 ${accent ? "text-[#047857]" : "text-[#111827]"}`}>
        {value}
      </div>
    </div>
  );
}

function ShareCard({ share }) {
  // Prefer the backend's branded tracking_url (uses PUBLIC_BASE_URL =
  // trackmatrix.net) so the affiliate copies the exact URL their shoppers
  // see, not the dashboard origin. Fallback synthesizes from current origin.
  const trackingUrl = useMemo(() => {
    if (share.tracking_url) return share.tracking_url;
    const base = _publicBase();
    const url = `${base}/r/${share.slug}`;
    return share.product_id ? `${url}?p=${encodeURIComponent(share.product_id)}` : url;
  }, [share.tracking_url, share.slug, share.product_id]);

  const landingUrl = useMemo(
    () =>
      computeLandingUrl({
        finalUrl: share.dest,
        slug: share.slug,
        productId: share.product_id,
      }),
    [share.dest, share.slug, share.product_id]
  );

  const [imageOk, setImageOk] = useState(true);

  return (
    <div className="card-flat p-4 sm:p-5 flex flex-col sm:flex-row gap-4" data-testid={`share-row-${share.product_id || share.link_id}`}>
      {/* Image */}
      <div className="flex-shrink-0">
        {share.image && imageOk ? (
          <img
            src={share.image}
            alt={share.title || "Product"}
            onError={() => setImageOk(false)}
            referrerPolicy="no-referrer"
            className="w-full sm:w-24 h-24 rounded-md object-cover bg-[#F3F4F6]"
          />
        ) : (
          <div className="w-full sm:w-24 h-24 rounded-md bg-[#F3F4F6] flex items-center justify-center text-[#9CA3AF]">
            <ShoppingBag size={22} />
          </div>
        )}
      </div>

      {/* Title + meta */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            {share.seller && (
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF] mb-0.5">{share.seller}</div>
            )}
            <div className="font-display font-black text-base text-[#111827] line-clamp-2">
              {share.title || share.product_id || "Product"}
            </div>
            <div className="text-xs text-[#4B5563] mt-1 flex items-center gap-2 flex-wrap">
              <span>Shared {_fmtWhen(share.last_shared_at)}</span>
              {share.share_count > 1 && (
                <span className="bg-[#F3F4F6] text-[#374151] rounded px-1.5 py-0.5 text-[10px] font-bold">
                  ×{share.share_count}
                </span>
              )}
              {share.price != null && <span>· {_fmtMoney(share.price, share.currency)}</span>}
              {share.commission_pct != null && <span>· {share.commission_pct}% commission</span>}
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4">
            <Stat label="Clicks" value={share.clicks || 0} />
            <Stat label="Conv." value={share.conversions || 0} highlight={share.conversions > 0} />
            <Stat
              label="Credit"
              value={_fmtMoney(share.credit_total || 0, share.currency || "GYD")}
              highlight={share.credit_total > 0}
              wide
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <button
            onClick={() => copyToClipboard(trackingUrl, "Tracking link copied")}
            data-testid={`share-row-copy-${share.product_id || share.link_id}`}
            className="text-xs font-bold text-[#002FA7] border border-[#E5E7EB] rounded-md px-3 py-1.5 hover:border-[#002FA7] inline-flex items-center gap-1.5"
          >
            <Copy size={11} /> Copy link
          </button>
          {landingUrl && (
            <a
              href={landingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`share-row-preview-${share.product_id || share.link_id}`}
              className="text-xs font-bold text-[#4B5563] border border-[#E5E7EB] rounded-md px-3 py-1.5 hover:border-[#111827] hover:text-[#111827] inline-flex items-center gap-1.5"
            >
              <ExternalLink size={11} /> Preview product
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight, wide }) {
  return (
    <div className={`text-right ${wide ? "min-w-[90px]" : "min-w-[50px]"}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF]">{label}</div>
      <div className={`font-display font-black text-lg ${highlight ? "text-[#047857]" : "text-[#111827]"}`}>
        {value}
      </div>
    </div>
  );
}
